<template>
    <Modal :show="show" @close="closeModal" label="$PageEditor$:add-page">
        <div class="flex flex-col w-full">
            <div class="mb-6">
                <h3 class="text-lg font-semibold text-gray-900 mb-4">
                    {{ parentId ? 'Add Child Page' : 'Add Root Page' }}
                </h3>
                <div v-if="parentId" class="bg-blue-50 p-3 rounded mb-4">
                    <p class="text-sm text-blue-700">
                        Adding child page to: <strong><Translate :value="parentPageName" /></strong>
                    </p>
                    <p class="text-xs text-blue-600 mt-1">
                        Parent ID: {{ parentId }}
                    </p>
                </div>
            </div>
            
            <ResponsiveGrid :columns="2" class="gap-4">
                <!-- Page Name Field -->
                <InputComponent
                    label="$PageEditor$:page-name"
                    name="pageName"
                    v-model="formData.name"
                    placeholder="Enter page name"
                    :disablePlaceholderTranslation="true"
                    required
                    :valid="formData.name.trim() && !routeExists ? true : (showErrors.name || routeExists ? false : undefined)"
                    @keyup.enter="submitForm"
                />
                
                <!-- View Component Field -->
                <InputComponent
                    label="$PageEditor$:view-component"
                    name="pageView"
                    v-model="formData.view"
                    placeholder="e.g. HomeView, SettingsView"
                    :disablePlaceholderTranslation="true"
                    required
                    :valid="formData.view.trim() ? true : (showErrors.view ? false : undefined)"
                    @keyup.enter="submitForm"
                />
            </ResponsiveGrid>
            
            <!-- Access Roles Field -->
            <div class="mb-6">
                <label for="pageAccess" class="block text-sm font-medium text-gray-700 h-[24px]">
                    <RequiredStar />
                    <Translate value="$PageEditor$:access-roles" />
                </label>
                
                <!-- Parent Access Info -->
                <div v-if="parentId && parentPageAccess" class="mb-3 p-3 bg-blue-50 rounded text-sm">
                    <p class="text-blue-700">
                        <strong>Parent Access:</strong> {{ parentPageAccess }} (Roles: {{ parentAccessRoles.join(', ') }})
                    </p>
                    <p class="text-blue-600 text-xs mt-1">
                        Child page can only use roles available to parent
                    </p>
                </div>
                
                <Select 
                    v-model="formData.access"
                    :options="availableRoleOptions"
                    valueKey="value"
                    textKey="label"
                    placeholder="Select roles..."
                    class="w-full"
                    :multiple="true"
                    :class="accessInputClass"
                    sortBy="value"
                    sortDirection="asc"
                />
                
                <!-- Access Preview -->
                <div v-if="formData.access.length > 0" class="mt-2 p-2 bg-gray-50 rounded text-sm">
                    <p class="text-gray-600">
                        <strong>Selected Roles:</strong> {{ selectedRoleNames.join(', ') || 'None' }}
                    </p>
                    <p class="text-gray-600">
                        <strong>Access String:</strong> {{ formattedAccessString }}
                    </p>
                </div>
                
                <!-- Access Messages -->
                <div v-if="hasInvalidAccess || (showErrors.access && formData.access.length === 0)" class="mt-2">
                    <p v-if="formData.access.length === 0 && showErrors.access" class="text-red-600 text-sm">
                        Access roles are required
                    </p>
                    <p v-if="hasInvalidAccess" class="text-red-600 text-sm">
                        Invalid roles: {{ invalidAccessRoles.join(', ') }}. Only roles {{ parentAccessRoles.join(', ') }} are allowed for this parent.
                    </p>
                </div>
                
                <p v-if="formData.access.length > 0 && !hasInvalidAccess" class="text-green-600 text-sm mt-2">
                    ✓ Access roles are valid
                </p>
            </div>
            
            <!-- Route Preview -->
            <div v-if="formData.name.trim()" class="mb-6 p-3 bg-gray-50 rounded">
                <h4 class="font-medium text-gray-900 mb-2">Preview:</h4>
                <div class="space-y-1 text-sm">
                    <p class="text-gray-600">
                        <strong>Title:</strong> <code class="bg-white px-1 rounded">{{`$page$:${formData.name.trim()}`}}</code>
                    </p>
                    <p class="text-gray-600">
                        <strong>Route:</strong> <code class="bg-white px-1 rounded">{{ generatedRoute }}</code>
                    </p>
                </div>
                
                <!-- Route Error -->
                <div v-if="routeExists" class="mt-2">
                    <p class="text-red-600 text-sm">
                        ⚠️ A page with route "{{ generatedRoute }}" already exists
                    </p>
                </div>
                
                <!-- Route Success -->
                <p v-if="!routeExists" class="text-green-600 text-sm mt-2">
                    ✓ Route is available
                </p>
            </div>
            
            <div class="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <Button 
                    @click="closeModal"
                    Color="gray-500"
                    TextColor="white">
                    Cancel
                </Button>
                <Button 
                    @click="submitForm"
                    :disabled="!formIsValid"
                    :Color="formIsValid ? 'main' : 'gray-300'"
                    :TextColor="formIsValid ? 'white' : 'gray-500'">
                    Add Page
                </Button>
            </div>
        </div>
    </Modal>
</template>

<script>
import Modal from '@/components/Blocks/Modal.vue'
import Button from '@/components/Blocks/Button.vue'
import InputComponent from '@/components/Form/InputComponent.vue'
import RequiredStar from '@/components/Form/RequiredStar.vue'
import ResponsiveGrid from '@/components/Blocks/ResponsiveGrid.vue'
import Select from '@/components/Blocks/Select.vue'
import FetchData from '@/utils/FetchData';

export default {
    name: 'AddPageModal',
    components: {
        Modal,
        Button,
        InputComponent,
        RequiredStar,
        ResponsiveGrid,
        Select
    },
    props: {
        show: {
            type: Boolean,
            default: false
        },
        parentId: {
            type: [Number, String],
            default: null
        },
        pages: {
            type: Array,
            default: () => []
        },
        roles: {
            type: Array,
            default: () => []
        }
    },
    emits: ['close', 'page-added'],
    data() {
        return {
            formData: {
                name: '',
                access: [],
                view: 'PageView'
            },
            showErrors: {
                name: false,
                access: false,
                view: false
            }
        }
    },
    computed: {
        parentPageName() {
            if (!this.parentId) return null;
            const parentPage = this.pages.find(page => page.id === this.parentId);
            return parentPage ? parentPage.title : null;
        },
        
        generatedRoute() {
            if (!this.formData.name.trim()) return '';
            return '/' + this.formData.name.trim().replace(/\s+/g, '-');
        },
        
        routeExists() {
            if (!this.formData.name.trim()) return false;
            const route = this.generatedRoute;
            return this.pages.some(page => page.route === route);
        },
        
        parentPageAccess() {
            if (!this.parentId) return null;
            const parentPage = this.pages.find(page => page.id === this.parentId);
            return parentPage ? parentPage.access : null;
        },
        
        parentAccessRoles() {
            if (!this.parentPageAccess) return [];
            // Handle both old format "123" and new format "$1$2$3$"
            if (this.parentPageAccess.toString().startsWith('$')) {
                // New format: "$1$2$3$" -> [1, 2, 3]
                return this.parentPageAccess.toString()
                    .split('$')
                    .filter(role => role !== '')
                    .map(role => parseInt(role))
                    .filter(role => !isNaN(role));
            } else {
                // Old format: "123" -> [1, 2, 3]
                return this.parentPageAccess.toString().split('').map(role => parseInt(role));
            }
        },
        
        currentAccessRoles() {
            return this.formData.access.map(role => parseInt(role)).filter(role => !isNaN(role));
        },
        
        selectedRoleNames() {
            return this.formData.access.map(roleId => {
                const role = this.roles.find(r => r.id === parseInt(roleId));
                return role ? this.$Translate(role.name) : "Unknown";
            });
        },
        
        formattedAccessString() {
            // Convert array [1, 2, 3] to new format "$1$2$3$"
            if (this.formData.access.length === 0) return '';
            return '$' + this.formData.access.join('$') + '$';
        },
        
        availableRoleOptions() {
            let availableRoles = this.roles;
            
            // If this is a child page, filter to only parent roles
            if (this.parentId && this.parentAccessRoles.length > 0) {
                availableRoles = this.roles.filter(role => 
                    this.parentAccessRoles.includes(role.id)
                );
            }
            
            return availableRoles.map(role => {
                // Business logic for protected and selected states
                const isAdminRole = role.id === 1; // Admin role (ID 1) is protected
                return {
                    value: role.id,
                    label: `${role.id} - ${this.$Translate(role.name)}`,
                    isProtected: isAdminRole, // Admin roles cannot be unchecked once selected
                    selected: this.formData.access.includes(role.id) || (isAdminRole && this.formData.access.length === 0) // Auto-select Admin if none selected
                };
            });
        },
        
        invalidAccessRoles() {
            if (!this.parentId) return []; // Root page, no restrictions
            const parentRoles = this.parentAccessRoles;
            return this.currentAccessRoles.filter(role => !parentRoles.includes(role));
        },
        
        hasInvalidAccess() {
            return this.invalidAccessRoles.length > 0;
        },
        
        accessInputClass() {
            const baseClass = 'mt-1 sm:mt-2 block w-full border shadow-sm focus:ring-main focus:border-main';
            if (this.hasInvalidAccess || (this.showErrors.access && this.formData.access.length === 0)) {
                return baseClass + ' border-red-500 bg-red-50';
            }
            if (this.formData.access.length > 0 && !this.hasInvalidAccess) {
                return baseClass + ' border-green-500 bg-green-50';
            }
            return baseClass + ' border-gray-400';
        },
        
        formIsValid() {
            return this.formData.name.trim() && 
                   !this.routeExists && 
                   this.formData.access.length > 0 && 
                   !this.hasInvalidAccess && 
                   this.formData.view.trim();
        }
    },
    watch: {
        show(newValue) {
            if (newValue) {
                this.resetForm();
            }
        },
        
        parentId(newValue) {
            if (newValue && this.parentPageAccess) {
                // Pre-select parent roles for child pages
                this.formData.access = [...this.parentAccessRoles];
            } else {
                this.formData.access = [];
            }
        }
    },
    methods: {
        closeModal() {
            this.resetForm();
            this.$emit('close');
        },
        
        resetForm() {
            this.formData = {
                name: '',
                access: this.parentId && this.parentPageAccess ? [...this.parentAccessRoles] : [],
                view: 'PageView'
            };
            this.showErrors = {
                name: false,
                access: false,
                view: false
            };
        },
        
        async submitForm() {
            // Reset errors
            this.showErrors = {
                name: false,
                access: false,
                view: false
            };
            
            // Validate form
            if (!this.formData.name.trim()) {
                this.showErrors.name = true;
                return;
            }
            
            if (this.formData.access.length === 0) {
                this.showErrors.access = true;
                return;
            }
            
            if (!this.formData.view.trim()) {
                this.showErrors.view = true;
                return;
            }
            
            if (this.routeExists || this.hasInvalidAccess) {
                return;
            }
            
            const pageName = this.formData.name.trim();
            const title = `$page$:${pageName}`;
            const route = this.generatedRoute;
            const access = this.formattedAccessString;
            const viewName = this.formData.view.trim();
            
            // Create payload in the requested format
            const payload = {
                "title": title,
                "route": route,
                "View": viewName,
                "parent": this.parentId,
                "access": access,
                "icon": "Tools", // Default icon, could be made configurable
            };
            
            try {
                // Send the payload to PostPages endpoint
                const response = await FetchData(payload, "PostPages", "POST");
                
                // Show success message if toast is available
                if (this.$toast && response.id) {
                    this.$toast.add({
                        severity: "success",
                        summary: "Success",
                        detail: "Page created successfully.",
                        life: 3000,
                    });
                }
                
                // Emit success event
                this.$emit('page-added', response);
                
                // Close modal after successful submission
                this.closeModal();
                
            } catch (error) {
                console.error('Error creating page:', error);
                
                // Show error message if toast is available
                if (this.$toast) {
                    this.$toast.add({
                        severity: "error",
                        summary: "Error",
                        detail: "Failed to create page. Please try again.",
                        life: 3000,
                    });
                }
            }
        }
    }
};
</script>